/**
 * KioskPage — NewAudia Event Lead Capture Kiosk
 *
 * Single-route page (/kiosk) optimized for iPad landscape.
 * Admin-gated entry. Once a session is active, the page enters kiosk
 * mode and the Layout chrome is hidden via the `data-kiosk-mode` flag
 * on <body> (read by App.js layout).
 *
 * State machine:
 *   admin_init   → admin enters event name/location and starts session
 *   welcome      → "Tap to begin" screen, waits for new lead
 *   capture      → contact + 13 capture screens, branched by role
 *   thank_you    → confirmation screen, auto-returns to welcome after 5s
 *   exit_confirm → admin password modal to end session
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Settings as GearIcon, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { ROLE_OPTIONS, getQuestionsForRole } from "./kiosk/kioskQuestions";

// =============================================================================
// CONSTANTS
// =============================================================================

const NAVY = "#1B3A6B";
const TEAL = "#00A896";
const PROMO_CODE = "GETNEWAUDIA";
const INACTIVITY_MS = 90 * 1000;
const THANK_YOU_COUNTDOWN_S = 10;

const NEW_LEAD_DRAFT = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  email_opt_in: true,
  sms_opt_in: false,
  role: "",
  // answers keyed by question.id; populated in capture screens 3+
  answers: {},
};

// Shared full-screen background style for capture + thank-you screens.
// Uses Robert's branded image with logo top-center and neon waves below.
// `cover` ensures the image fills any iPad viewport; the logo's bottom edge
// sits at roughly 33% of the viewport height after cover-scaling, so all
// inner content is held below ~35% via SCREEN_TOP_SAFEZONE_VH.
const SCREEN_BG_STYLE = {
  backgroundImage: "url(/kiosk-screen-bg.png?v=1)",
  backgroundSize: "cover",
  backgroundPosition: "center center",
  backgroundRepeat: "no-repeat",
  backgroundColor: NAVY,  // shows during image load
};
const SCREEN_TOP_SAFEZONE_VH = 28;  // % of viewport reserved for the bg logo

// Total capture screens before role-branched questions:
//   0 = Name, 1 = Email, 2 = Phone+SMS+Role
// After the role is picked we get 10 role-branched questions.
// Total = 13 screens.

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function KioskPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  const [mode, setMode] = useState("loading"); // loading|admin_init|welcome|capture|thank_you
  const [session, setSession] = useState(null); // {session_id, event_name, event_location, lead_count}
  const [showExitModal, setShowExitModal] = useState(false);
  const [bootError, setBootError] = useState("");

  const isAdmin = isAuthenticated && (user?.roles || []).includes("admin");

  // Toggle a body flag so the App layout (Header/Footer/Sidebars) can hide chrome.
  useEffect(() => {
    const isKioskActive = mode === "welcome" || mode === "capture" || mode === "thank_you";
    if (isKioskActive) {
      document.body.setAttribute("data-kiosk-mode", "active");
    } else {
      document.body.removeAttribute("data-kiosk-mode");
    }
    return () => document.body.removeAttribute("data-kiosk-mode");
  }, [mode]);

  // Lock-down: prevent accidental browser-back during kiosk modes.
  useEffect(() => {
    if (mode !== "welcome" && mode !== "capture" && mode !== "thank_you") return;
    const onPop = () => {
      // Push state back onto the stack so back-swipes stay on /kiosk.
      window.history.pushState(null, "", window.location.href);
    };
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [mode]);

  // Bootstrap: redirect non-admins, fetch any active session
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    if (!isAdmin) {
      navigate("/");
      return;
    }
    (async () => {
      try {
        const res = await api.get("/kiosk/session-status");
        const active = res.data?.active_session;
        if (active) {
          setSession(active);
          setMode("welcome");
        } else {
          setMode("admin_init");
        }
      } catch (err) {
        console.error("[KIOSK] session-status failed", err);
        setBootError("Unable to load kiosk status. Please try again.");
        setMode("admin_init");
      }
    })();
  }, [authLoading, isAuthenticated, isAdmin, navigate]);

  const handleSessionStarted = (sess) => {
    setSession({ ...sess, lead_count: 0 });
    setMode("welcome");
  };

  const handleSessionEnded = () => {
    setShowExitModal(false);
    setSession(null);
    navigate("/admin");
  };

  const handleLeadSubmitted = () => {
    setSession((s) => (s ? { ...s, lead_count: (s.lead_count || 0) + 1 } : s));
    setMode("thank_you");
  };

  if (mode === "loading") {
    return <FullScreenLoader />;
  }

  return (
    <div data-testid="kiosk-page" style={{ minHeight: "100vh", background: NAVY, color: "#fff" }}>
      {mode === "admin_init" && (
        <AdminInitScreen onStarted={handleSessionStarted} bootError={bootError} />
      )}

      {mode === "welcome" && session && (
        <WelcomeScreen
          session={session}
          onTapBegin={() => setMode("capture")}
          onExitClick={() => setShowExitModal(true)}
        />
      )}

      {mode === "capture" && session && (
        <CaptureFlow
          session={session}
          onTimeout={() => setMode("welcome")}
          onCancel={() => setMode("welcome")}
          onSubmitted={handleLeadSubmitted}
        />
      )}

      {mode === "thank_you" && (
        <ThankYouScreen onComplete={() => setMode("welcome")} />
      )}

      {showExitModal && session && (
        <ExitConfirmModal
          sessionId={session.session_id}
          onCancel={() => setShowExitModal(false)}
          onEnded={handleSessionEnded}
        />
      )}
    </div>
  );
}

// =============================================================================
// FULL-SCREEN LOADER
// =============================================================================

function FullScreenLoader() {
  return (
    <div
      style={{ minHeight: "100vh", background: NAVY, display: "flex",
               alignItems: "center", justifyContent: "center" }}
      data-testid="kiosk-loading"
    >
      <Loader2 className="animate-spin" size={48} color="#fff" />
    </div>
  );
}

// =============================================================================
// ADMIN INIT SCREEN
// =============================================================================

function AdminInitScreen({ onStarted, bootError }) {
  const [eventName, setEventName] = useState("");
  const [eventLocation, setEventLocation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleStart = async (e) => {
    e.preventDefault();
    if (!eventName.trim()) {
      setError("Event name is required.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await api.post("/kiosk/start-session", {
        event_name: eventName.trim(),
        event_location: eventLocation.trim() || null,
      });
      onStarted({
        session_id: res.data.session_id,
        event_name: res.data.event_name,
        event_location: res.data.event_location,
        started_at: res.data.started_at,
      });
    } catch (err) {
      console.error("[KIOSK] start-session failed", err);
      setError(err?.response?.data?.detail || "Unable to start session. Try again.");
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{ minHeight: "100vh", display: "flex", alignItems: "center",
               justifyContent: "center", padding: "32px" }}
      data-testid="kiosk-admin-init"
    >
      <form
        onSubmit={handleStart}
        style={{
          background: "#fff", color: NAVY, padding: "48px 56px", borderRadius: 12,
          boxShadow: "0 12px 48px rgba(0,0,0,0.3)", width: "100%", maxWidth: 560,
        }}
      >
        <img
          src="/newaudia-logo.jpg"
          alt="NewAudia"
          style={{ height: 56, marginBottom: 24 }}
        />
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 32, margin: "0 0 8px 0" }}>
          Start Kiosk Session
        </h1>
        <p style={{ color: "#666", marginBottom: 32, fontSize: 14 }}>
          You're signed in as an admin. Configure this kiosk for your event below.
        </p>

        {bootError && (
          <div
            style={{ background: "#fff5f5", color: "#a01717", padding: 12,
                     borderRadius: 6, marginBottom: 16, fontSize: 14 }}
            data-testid="kiosk-boot-error"
          >
            {bootError}
          </div>
        )}

        <label style={{ display: "block", fontWeight: 600, fontSize: 15, marginBottom: 6 }}>
          Event Name *
        </label>
        <input
          type="text"
          value={eventName}
          onChange={(e) => setEventName(e.target.value)}
          placeholder="Pitch Competition — Colorado Springs"
          required
          maxLength={200}
          autoFocus
          data-testid="kiosk-event-name-input"
          style={{ width: "100%", padding: "16px 18px", fontSize: 18, border: "2px solid #e0e0e0",
                   borderRadius: 8, marginBottom: 20, fontFamily: "Arial, sans-serif" }}
        />

        <label style={{ display: "block", fontWeight: 600, fontSize: 15, marginBottom: 6 }}>
          Event Location <span style={{ color: "#999", fontWeight: 400 }}>(optional)</span>
        </label>
        <input
          type="text"
          value={eventLocation}
          onChange={(e) => setEventLocation(e.target.value)}
          placeholder="Venue or city"
          maxLength={200}
          data-testid="kiosk-event-location-input"
          style={{ width: "100%", padding: "16px 18px", fontSize: 18, border: "2px solid #e0e0e0",
                   borderRadius: 8, marginBottom: 28, fontFamily: "Arial, sans-serif" }}
        />

        {error && (
          <div
            style={{ background: "#fff5f5", color: "#a01717", padding: 12, borderRadius: 6,
                     marginBottom: 16, fontSize: 14 }}
            data-testid="kiosk-admin-init-error"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="kiosk-start-session-btn"
          style={{ width: "100%", padding: "18px", fontSize: 18, fontWeight: 700,
                   background: TEAL, color: "#fff", border: "none", borderRadius: 8,
                   cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.7 : 1 }}
        >
          {submitting ? "Starting…" : "Start Session"}
        </button>
      </form>
    </div>
  );
}

// =============================================================================
// WELCOME SCREEN
// =============================================================================

function WelcomeScreen({ session, onTapBegin, onExitClick }) {
  // Baked-in "Tap to Begin" button coordinates inside the graphic
  // (computed by scanning the teal fill of the button; image is 1023×516).
  //   center: 58.02% x 68.02%
  //   size:   16.13% × 9.30%
  // We expand the hit area slightly for finger-friendliness on iPad.
  const HIT_CENTER_X = 58.02;
  const HIT_CENTER_Y = 68.02;
  const HIT_W = 22;   // a touch wider than the painted button for easier tapping
  const HIT_H = 14;   // a touch taller ditto

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100vw",
        background: NAVY,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
      data-testid="kiosk-welcome"
    >
      {/* Image-aspect inner container (2:1) — the entire welcome canvas.
          The graphic carries all the typography, so we don't render any text
          ourselves. Inside this container, percentage positions map 1:1 to
          coordinates inside the image. */}
      <div
        style={{
          position: "relative",
          // iPad landscape (1180×820 ≈ 1.44:1) is taller than the 2:1 image.
          // With width:100%, height auto, the image fits the full width with
          // narrow navy bands top and bottom. Those bands match the image's
          // inherent dark navy background so the seam is invisible.
          width: "100%",
          maxHeight: "100vh",
          aspectRatio: "1023 / 516",
          backgroundImage: "url(/kiosk-welcome-bg.png?v=2)",
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center center",
        }}
      >
        {/* Invisible hit target perfectly overlaying the painted Tap to Begin */}
        <button
          onClick={onTapBegin}
          aria-label="Tap to begin"
          data-testid="kiosk-tap-to-begin-btn"
          style={{
            position: "absolute",
            left: `${HIT_CENTER_X - HIT_W / 2}%`,
            top: `${HIT_CENTER_Y - HIT_H / 2}%`,
            width: `${HIT_W}%`,
            height: `${HIT_H}%`,
            background: "transparent",
            border: "none",
            borderRadius: 12,
            cursor: "pointer",
            padding: 0,
          }}
        />
      </div>

      {/* Admin exit (small gear, low contrast so it doesn't pull focus) */}
      <button
        onClick={onExitClick}
        aria-label="Admin exit"
        data-testid="kiosk-exit-gear-btn"
        style={{
          position: "absolute",
          left: 24,
          bottom: 24,
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.35)",
          cursor: "pointer",
          padding: 10,
          borderRadius: 8,
        }}
      >
        <GearIcon size={28} />
      </button>

      {/* Session stats (bottom-right, discreet) */}
      <div
        style={{
          position: "absolute",
          right: 24,
          bottom: 24,
          fontSize: 13,
          color: "rgba(255,255,255,0.55)",
          textAlign: "right",
          fontFamily: "Arial, sans-serif",
          letterSpacing: "0.02em",
        }}
        data-testid="kiosk-session-stats"
      >
        {session.event_name} — {session.lead_count || 0} lead{(session.lead_count || 0) === 1 ? "" : "s"} captured
      </div>
    </div>
  );
}

// =============================================================================
// CAPTURE FLOW (handles screens 0..N)
// =============================================================================

function CaptureFlow({ session, onTimeout, onCancel, onSubmitted }) {
  const [draft, setDraft] = useState({ ...NEW_LEAD_DRAFT });
  const [screenIdx, setScreenIdx] = useState(0); // 0=name, 1=email, 2=role, 3+=role questions
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Inactivity timer
  const timeoutRef = useRef(null);
  const resetInactivity = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => onTimeout(), INACTIVITY_MS);
  }, [onTimeout]);

  useEffect(() => {
    resetInactivity();
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [resetInactivity]);

  // Reset inactivity on any user input
  useEffect(() => {
    const handler = () => resetInactivity();
    window.addEventListener("touchstart", handler, { passive: true });
    window.addEventListener("mousedown", handler);
    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("touchstart", handler);
      window.removeEventListener("mousedown", handler);
      window.removeEventListener("keydown", handler);
    };
  }, [resetInactivity]);

  const roleQuestions = useMemo(() => getQuestionsForRole(draft.role), [draft.role]);
  const totalScreens = 3 + roleQuestions.length; // 3 contact screens + role-branched questions

  const updateDraft = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setAnswer = (questionId, value) =>
    setDraft((d) => ({ ...d, answers: { ...d.answers, [questionId]: value } }));

  // NOTE: do NOT clamp goNext against totalScreens — totalScreens depends on
  // role, which may have just been set in the same tick. Allow the index to
  // advance and let render-side guards no-op past the end.
  const goNext = useCallback(() => setScreenIdx((i) => i + 1), []);
  const goBack = useCallback(() => setScreenIdx((i) => Math.max(i - 1, 0)), []);

  const isLast = !!draft.role && screenIdx === totalScreens - 1;

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      // Build responses from answers map, in the order of the role's question list
      const responses = roleQuestions.map((q) => ({
        question_id: q.id,
        question_text: q.text,
        answer: draft.answers[q.id] ?? (q.answer_type === "multi" ? [] : ""),
        answer_type: q.answer_type,
      }));
      await api.post("/kiosk/submit-lead", {
        session_id: session.session_id,
        first_name: draft.first_name.trim(),
        last_name: draft.last_name.trim(),
        email: draft.email.trim().toLowerCase(),
        phone: draft.phone.trim() || null,
        email_opt_in: !!draft.email_opt_in,
        sms_opt_in: !!draft.sms_opt_in,
        role: draft.role,
        responses,
      });
      onSubmitted();
    } catch (err) {
      console.error("[KIOSK] submit-lead failed", err);
      setSubmitError(
        err?.response?.data?.detail ||
        "Connection issue. Tap Try Again to retry — your answers are saved."
      );
      setSubmitting(false);
    }
  };

  // Progress
  const stepNumber = screenIdx + 1;
  const progressPct = Math.round((stepNumber / totalScreens) * 100);

  return (
    <div
      style={{
        height: "100vh",
        maxHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        padding: "20px 40px 16px",
        overflow: "hidden",
        ...SCREEN_BG_STYLE,
      }}
      data-testid="kiosk-capture"
    >
      <CaptureHeader stepNumber={stepNumber} totalScreens={totalScreens} progressPct={progressPct} />

      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center",
          minHeight: 0,        // allow children to shrink below their intrinsic height
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 980,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {screenIdx === 0 && (
            <NameScreen draft={draft} onChange={updateDraft} onContinue={goNext} />
          )}
          {screenIdx === 1 && (
            <EmailScreen draft={draft} onChange={updateDraft} onContinue={goNext} />
          )}
          {screenIdx === 2 && (
            <PhoneRoleScreen draft={draft} onChange={updateDraft} onRolePicked={goNext} />
          )}
          {screenIdx >= 3 && roleQuestions[screenIdx - 3] && (
            <QuestionScreen
              question={roleQuestions[screenIdx - 3]}
              value={draft.answers[roleQuestions[screenIdx - 3].id]}
              onChange={(v) => setAnswer(roleQuestions[screenIdx - 3].id, v)}
              onContinue={isLast ? handleSubmit : goNext}
              isLast={isLast}
              submitting={submitting}
            />
          )}
        </div>
      </div>

      {submitError && (
        <div
          data-testid="kiosk-submit-error"
          style={{ background: "rgba(255,80,80,0.15)", color: "#ffb3b3", padding: 16,
                   borderRadius: 8, margin: "12px auto", maxWidth: 600, textAlign: "center" }}
        >
          {submitError}
          <button
            onClick={handleSubmit}
            data-testid="kiosk-submit-retry-btn"
            style={{ marginLeft: 16, padding: "8px 16px", background: TEAL, color: "#fff",
                     border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 600 }}
          >
            Try Again
          </button>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 16 }}>
        <button
          onClick={screenIdx === 0 ? onCancel : goBack}
          data-testid="kiosk-back-btn"
          style={{ padding: "16px 28px", fontSize: 18, background: "rgba(255,255,255,0.1)",
                   color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 8,
                   cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
        >
          <ArrowLeft size={20} />
          {screenIdx === 0 ? "Cancel" : "Back"}
        </button>
        <div style={{ fontSize: 14, color: "rgba(255,255,255,0.6)" }}>
          {session.event_name}
        </div>
      </div>
    </div>
  );
}

function CaptureHeader({ stepNumber, totalScreens, progressPct }) {
  // The new background image carries the big NewAudia.com logo in the top-center.
  // We hold the progress strip BELOW the logo's safe zone so the two never overlap.
  return (
    <div style={{ marginTop: `${SCREEN_TOP_SAFEZONE_VH}vh`, marginBottom: 16, flexShrink: 0 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end",
                    marginBottom: 8 }}>
        <div data-testid="kiosk-progress-label"
             style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", fontWeight: 600,
                      letterSpacing: "0.04em", textTransform: "uppercase" }}>
          Step {stepNumber} of {totalScreens}
        </div>
      </div>
      <div style={{ height: 5, background: "rgba(255,255,255,0.18)", borderRadius: 3,
                    overflow: "hidden" }}>
        <div data-testid="kiosk-progress-bar"
             style={{ width: `${progressPct}%`, height: "100%", background: TEAL,
                      transition: "width 240ms ease" }} />
      </div>
    </div>
  );
}

// =============================================================================
// CAPTURE — SCREEN 0: Name
// =============================================================================

function NameScreen({ draft, onChange, onContinue }) {
  const ready = draft.first_name.trim() && draft.last_name.trim();
  return (
    <div data-testid="kiosk-screen-name" style={SCREEN_BODY_FLEX}>
      <h2 style={H2_STYLE}>What's your name?</h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginTop: 24 }}>
        <BigInput
          label="First name"
          value={draft.first_name}
          onChange={(v) => onChange({ first_name: v })}
          autoFocus
          testId="kiosk-input-first-name"
        />
        <BigInput
          label="Last name"
          value={draft.last_name}
          onChange={(v) => onChange({ last_name: v })}
          testId="kiosk-input-last-name"
        />
      </div>
      <div style={{ flex: 1 }} />
      <ContinueRow disabled={!ready} onClick={onContinue} testId="kiosk-name-continue-btn" />
    </div>
  );
}

// =============================================================================
// CAPTURE — SCREEN 1: Email + opt-in
// =============================================================================

function EmailScreen({ draft, onChange, onContinue }) {
  const [touched, setTouched] = useState(false);
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim());
  return (
    <div data-testid="kiosk-screen-email" style={SCREEN_BODY_FLEX}>
      <h2 style={H2_STYLE}>What's your email?</h2>
      <div style={{ marginTop: 24 }}>
        <BigInput
          type="email"
          label="Email address"
          value={draft.email}
          onChange={(v) => onChange({ email: v })}
          onBlur={() => setTouched(true)}
          autoFocus
          testId="kiosk-input-email"
          error={touched && !valid ? "Please enter a valid email address." : null}
        />
      </div>
      <BigCheckbox
        checked={draft.email_opt_in}
        onChange={(v) => onChange({ email_opt_in: v })}
        label="Yes, I'd like to receive NewAudia updates and announcements."
        testId="kiosk-email-optin-checkbox"
      />
      <div style={{ flex: 1 }} />
      <ContinueRow disabled={!valid} onClick={onContinue} testId="kiosk-email-continue-btn" />
    </div>
  );
}

// =============================================================================
// CAPTURE — SCREEN 2: Phone, SMS opt-in, Role (auto-advance on role pick)
// =============================================================================

function PhoneRoleScreen({ draft, onChange, onRolePicked }) {
  const phoneEmpty = !draft.phone.trim();
  const handleRole = (role) => {
    onChange({ role });
    setTimeout(onRolePicked, 400);
  };
  return (
    <div data-testid="kiosk-screen-phone-role" style={SCREEN_BODY_FLEX}>
      <h2 style={H2_STYLE}>A few last details…</h2>

      <div style={{ marginTop: 18 }}>
        <BigInput
          type="tel"
          label="Phone number (optional)"
          value={draft.phone}
          onChange={(v) => onChange({ phone: v })}
          testId="kiosk-input-phone"
        />
      </div>

      <BigCheckbox
        checked={!phoneEmpty && draft.sms_opt_in}
        onChange={(v) => onChange({ sms_opt_in: v })}
        disabled={phoneEmpty}
        label="OK to send me text updates"
        testId="kiosk-sms-optin-checkbox"
      />

      <h3 style={{ fontFamily: "Georgia, serif", fontSize: 24, marginTop: 18, marginBottom: 12,
                   flexShrink: 0 }}>
        Which best describes you?
      </h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                    gridAutoRows: "1fr", gap: 12, flex: 1, minHeight: 0 }}>
        {ROLE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => handleRole(opt.value)}
            data-testid={`kiosk-role-${opt.value}-btn`}
            style={{
              ...BIG_OPTION_BTN,
              fontSize: 22,
              padding: "16px 20px",
              ...(draft.role === opt.value ? BIG_OPTION_BTN_SELECTED : {}),
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// CAPTURE — Generic question screen (single/multi/text)
// =============================================================================

function QuestionScreen({ question, value, onChange, onContinue, isLast, submitting }) {
  const isMulti = question.answer_type === "multi";
  const isText = question.answer_type === "text";
  const selected = value ?? (isMulti ? [] : "");

  const toggleMulti = (opt) => {
    const next = selected.includes(opt) ? selected.filter((o) => o !== opt) : [...selected, opt];
    onChange(next);
  };

  const pickSingle = (opt) => {
    onChange(opt);
    setTimeout(onContinue, 250);
  };

  const ready = isText
    ? question.skippable || (typeof selected === "string" && selected.trim().length > 0)
    : isMulti
      ? selected.length > 0
      : !!selected;

  // Density scales button size based on number of options to keep everything in one viewport
  const numOpts = !isText ? question.options.length : 0;
  const cols = numOpts > 4 ? 2 : 1;
  const rows = Math.ceil(numOpts / cols);
  // The denser the grid, the smaller the button typography
  const btnFontSize = rows >= 4 ? 19 : rows === 3 ? 21 : 24;
  const btnPadding = rows >= 4 ? "12px 18px" : rows === 3 ? "16px 20px" : "20px 22px";
  const headingSize = rows >= 4 ? 30 : 36;

  return (
    <div data-testid={`kiosk-question-${question.id}`} style={SCREEN_BODY_FLEX}>
      <h2 style={{ ...H2_STYLE, fontSize: headingSize }}>{question.text}</h2>

      {!isText && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: cols === 2 ? "1fr 1fr" : "1fr",
            gridAutoRows: "1fr",            // each row shares the available height equally
            gap: rows >= 4 ? 10 : 14,
            marginTop: 16,
            flex: 1,                         // fill the remaining space
            minHeight: 0,
          }}
        >
          {question.options.map((opt) => {
            const isSelected = isMulti ? selected.includes(opt) : selected === opt;
            return (
              <button
                key={opt}
                onClick={() => (isMulti ? toggleMulti(opt) : pickSingle(opt))}
                data-testid={`kiosk-opt-${question.id}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                style={{
                  ...BIG_OPTION_BTN,
                  fontSize: btnFontSize,
                  padding: btnPadding,
                  minHeight: 0,              // let grid determine height
                  ...(isSelected ? BIG_OPTION_BTN_SELECTED : {}),
                }}
              >
                {opt}
              </button>
            );
          })}
        </div>
      )}

      {isText && (
        <textarea
          value={typeof selected === "string" ? selected : ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Tap to type your answer…"
          data-testid={`kiosk-text-${question.id}`}
          style={{ width: "100%", marginTop: 16, padding: "16px 20px", fontSize: 19,
                   borderRadius: 10, border: "2px solid rgba(255,255,255,0.2)",
                   background: "rgba(255,255,255,0.08)", color: "#fff",
                   fontFamily: "Arial, sans-serif", resize: "none", flex: 1,
                   minHeight: 0 }}
        />
      )}

      {(isMulti || isText) && (
        <ContinueRow
          disabled={!ready || submitting}
          onClick={onContinue}
          label={isLast ? (submitting ? "Submitting…" : "Submit") : "Continue"}
          testId={`kiosk-q-${question.id}-continue-btn`}
          showSkip={isText && question.skippable}
          onSkip={isText && question.skippable ? () => { onChange(""); onContinue(); } : null}
        />
      )}
    </div>
  );
}

// =============================================================================
// CAPTURE — shared input/option styles
// =============================================================================

const SCREEN_BODY_FLEX = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
};

const H2_STYLE = {
  fontFamily: "Georgia, serif",
  fontSize: 36,
  margin: 0,
  letterSpacing: "-0.01em",
  lineHeight: 1.2,
  flexShrink: 0,
};

const BIG_OPTION_BTN = {
  padding: "20px 22px",
  fontSize: 24,
  fontFamily: "Arial, sans-serif",
  background: "rgba(255,255,255,0.08)",
  color: "#fff",
  border: "2px solid rgba(255,255,255,0.2)",
  borderRadius: 12,
  cursor: "pointer",
  textAlign: "left",
  transition: "background-color 160ms ease, border-color 160ms ease",
  // Truncate any overflowing text rather than wrapping into 2 lines that blow up height
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "flex",
  alignItems: "center",
};

const BIG_OPTION_BTN_SELECTED = {
  background: TEAL,
  borderColor: TEAL,
  fontWeight: 700,
};

function BigInput({ label, value, onChange, type = "text", autoFocus, testId, onBlur, error }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 15, color: "rgba(255,255,255,0.7)",
                      marginBottom: 6, fontFamily: "Arial, sans-serif" }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        autoFocus={autoFocus}
        data-testid={testId}
        style={{ width: "100%", padding: "16px 20px", fontSize: 22,
                 background: "rgba(255,255,255,0.08)", color: "#fff",
                 border: error ? "2px solid #ff8a8a" : "2px solid rgba(255,255,255,0.2)",
                 borderRadius: 10, fontFamily: "Arial, sans-serif" }}
      />
      {error && (
        <div style={{ color: "#ff8a8a", fontSize: 13, marginTop: 6 }}>{error}</div>
      )}
    </div>
  );
}

function BigCheckbox({ checked, onChange, label, disabled, testId }) {
  return (
    <label
      data-testid={`${testId}-label`}
      style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 14,
               cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1,
               padding: "8px 4px", flexShrink: 0 }}
    >
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        data-testid={testId}
        style={{ width: 24, height: 24, accentColor: TEAL, cursor: disabled ? "not-allowed" : "pointer" }}
      />
      <span style={{ fontSize: 16, color: "rgba(255,255,255,0.9)" }}>{label}</span>
    </label>
  );
}

function ContinueRow({ disabled, onClick, label = "Continue", testId, showSkip, onSkip }) {
  return (
    <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end", gap: 10,
                  flexShrink: 0 }}>
      {showSkip && (
        <button
          onClick={onSkip}
          data-testid={`${testId}-skip`}
          style={{ padding: "14px 24px", fontSize: 18, background: "transparent",
                   color: "rgba(255,255,255,0.7)", border: "1px solid rgba(255,255,255,0.3)",
                   borderRadius: 8, cursor: "pointer" }}
        >
          Skip
        </button>
      )}
      <button
        onClick={onClick}
        disabled={disabled}
        data-testid={testId}
        style={{ padding: "14px 32px", fontSize: 20, fontWeight: 700,
                 background: disabled ? "rgba(0,168,150,0.4)" : TEAL, color: "#fff",
                 border: "none", borderRadius: 10,
                 cursor: disabled ? "not-allowed" : "pointer",
                 display: "flex", alignItems: "center", gap: 10 }}
      >
        {label}
        <ArrowRight size={20} />
      </button>
    </div>
  );
}

// =============================================================================
// THANK YOU SCREEN
// =============================================================================

function ThankYouScreen({ onComplete }) {
  const [secondsLeft, setSecondsLeft] = useState(THANK_YOU_COUNTDOWN_S);

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval);
          onComplete();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [onComplete]);

  return (
    <div
      onClick={onComplete}
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        // Push content below the safe zone so the bg logo at top is never covered
        justifyContent: "flex-start",
        paddingTop: `${SCREEN_TOP_SAFEZONE_VH + 2}vh`,
        paddingLeft: 48,
        paddingRight: 48,
        paddingBottom: 48,
        textAlign: "center",
        cursor: "pointer",
        ...SCREEN_BG_STYLE,
      }}
      data-testid="kiosk-thank-you"
    >
      <h1 style={{ fontFamily: "Georgia, serif", fontSize: 52, margin: 0,
                   maxWidth: 900, lineHeight: 1.2 }}>
        Thanks for your interest in NewAudia!
      </h1>
      <p style={{ fontSize: 22, color: "rgba(255,255,255,0.85)", marginTop: 18,
                  maxWidth: 720, lineHeight: 1.5 }}>
        You can complete your registration and start using the app on your own device.
      </p>
      <div
        style={{ marginTop: 36, padding: "28px 48px", background: "rgba(0,0,0,0.45)",
                 border: `2px solid ${TEAL}`, borderRadius: 16, maxWidth: 720,
                 backdropFilter: "blur(4px)" }}
        data-testid="kiosk-thankyou-promo"
      >
        <p style={{ fontSize: 22, margin: 0, color: "#fff" }}>
          Visit <strong>www.NewAudia.com</strong> TODAY
        </p>
        <p style={{ fontSize: 17, margin: "10px 0 0 0", color: "rgba(255,255,255,0.85)" }}>
          Use promo code{" "}
          <span style={{ fontWeight: 700, color: TEAL, padding: "4px 12px",
                         background: "rgba(0,168,150,0.22)", borderRadius: 6,
                         fontSize: 20 }}>
            {PROMO_CODE}
          </span>{" "}
          for 25 free Reader Credits
        </p>
      </div>
      <p style={{ marginTop: 28, fontSize: 14, color: "rgba(255,255,255,0.55)" }}
         data-testid="kiosk-thankyou-countdown">
        Returning to start in {secondsLeft}…  (tap anywhere to continue)
      </p>
    </div>
  );
}

// =============================================================================
// EXIT CONFIRM MODAL
// =============================================================================

function ExitConfirmModal({ sessionId, onCancel, onEnded }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/kiosk/end-session", {
        session_id: sessionId,
        admin_password: password,
      });
      onEnded();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setError("Incorrect password.");
      } else {
        setError(err?.response?.data?.detail || "Unable to end session. Try again.");
      }
      setPassword("");
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
               display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50,
               padding: 24 }}
      data-testid="kiosk-exit-modal"
    >
      <form
        onSubmit={handleSubmit}
        style={{ background: "#fff", color: NAVY, padding: "40px 48px", borderRadius: 12,
                 width: "100%", maxWidth: 460, boxShadow: "0 16px 48px rgba(0,0,0,0.4)" }}
      >
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 26, margin: "0 0 8px 0" }}>
          End Kiosk Session
        </h2>
        <p style={{ color: "#666", fontSize: 14, marginBottom: 24 }}>
          Enter your admin password to exit kiosk mode.
        </p>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Admin password"
          autoFocus
          required
          data-testid="kiosk-exit-password-input"
          style={{ width: "100%", padding: "14px 16px", fontSize: 17,
                   border: "2px solid #e0e0e0", borderRadius: 8, marginBottom: 16,
                   fontFamily: "Arial, sans-serif" }}
        />
        {error && (
          <div data-testid="kiosk-exit-error"
               style={{ background: "#fff5f5", color: "#a01717", padding: 10,
                        borderRadius: 6, marginBottom: 16, fontSize: 14 }}>
            {error}
          </div>
        )}
        <div style={{ display: "flex", gap: 12 }}>
          <button
            type="button"
            onClick={onCancel}
            data-testid="kiosk-exit-cancel-btn"
            style={{ flex: 1, padding: "14px", fontSize: 16, background: "#f0f0f0",
                     color: "#333", border: "none", borderRadius: 8, cursor: "pointer",
                     fontWeight: 600 }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !password}
            data-testid="kiosk-exit-confirm-btn"
            style={{ flex: 1, padding: "14px", fontSize: 16, background: NAVY,
                     color: "#fff", border: "none", borderRadius: 8,
                     cursor: submitting ? "not-allowed" : "pointer", fontWeight: 700,
                     opacity: submitting || !password ? 0.6 : 1 }}
          >
            {submitting ? "Ending…" : "End Session"}
          </button>
        </div>
      </form>
    </div>
  );
}
